import type { FC, ReactNode } from 'react'
import { FileText, CheckCircle2, Download, Calendar, PenLine } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Loader } from '@/components/ui'
import { useDocumentStats } from '@/lib/api/hooks/document'
import formatDate from '@/utils/helpers/formatDate'

interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
	description?: string
}

interface StatConfig {
	key: string
	title: string
	icon: LucideIcon
	iconContainerClassName: string
	iconClassName: string
	description?: string
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
	description
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 xl:w-16 xl:h-16 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div className="flex flex-col gap-1">
					<p className="text-base font-medium text-gray-600">{title}</p>
			  <p className={`leading-[1.1] font-semibold text-gray-900 ${title.toLowerCase() === 'last updated' ? 'text-[1.25rem]' : 'text-[2rem]'}`}>{value}</p>
					{description && <p className="text-sm text-gray-500 ">{description}</p>}
				</div>
			</div>
		</article>
	)
}

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Documents',
		icon: FileText,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		description: ''
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		description: ''
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
		description: ''
	},
	{
		key: 'totalDownloads',
		title: 'Downloads',
		icon: Download,
   iconContainerClassName: 'bg-[#9333ea10]',
    iconClassName: 'text-[#9333ea]',
		description: ''
	},
	{
		key: 'lastUpdatedDate',
		title: 'Last Updated',
		icon: Calendar,
    iconContainerClassName: 'bg-[#3389ea10]',
    iconClassName: 'text-[#3389ea]',
		description: ''
	},
]

const ProcurementDocumentStats: FC = () => {
	const { data: stats, isLoading, refetch, isError } = useDocumentStats()

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-5">
				<p>Error loading statistics.</p>
				<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
					Retry
				</button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const values = {
		total: stats?.totalDocuments?.totalCount ?? 0,
		totalPublished: stats?.publishedDocuments?.totalCount ?? 0,
		totalDrafts: stats?.draftDocuments?.totalCount ?? 0,
		totalDownloads: stats?.totalDownloads ?? 0,
		lastUpdatedDate: stats?.latestUpdatedAt ? formatDate(stats.latestUpdatedAt, 'DD MMM YYYY') : '-',
	}

	const getDescription = (key: string) => {
		switch (key) {
			case 'total':
				return 'All Time'
				case "totalPublished":
					return `${stats?.publishedDocuments?.percentageChange ?? 0}% of total`
			case 'lastUpdatedDate':
				return ''
				case 'totalDrafts':
					return "Pending Review"
					case "totalDownloads":
						return "All time"
			default:
				return ''
		}
	}

	return (
		<section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 2xl:w-3/4">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						title={config.title}
						value={values[config.key as keyof typeof values]}
						icon={<Icon className="w-6 h-6 xl:w-8 xl:h-8" strokeWidth={1.5} />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
						description={getDescription(config.key)}
					/>
				)
			})}
		</section>
	)
}

export default ProcurementDocumentStats
